// ============================================================================
// @orgazone/money-format — RENDU HUMAIN d'un montant (SPEC §24.6ter). UTILITÉ : produire la
// chaîne affichée d'une valeur monétaire (« 69,99 € », « 1 234,56 », « -5,00 € ») de façon
// IDENTIQUE partout — caisse (ui-native), tickets, back-office web, futurs produits — locale et
// devise paramétrées. Satellite de @orgazone/core-money (il en dépend pour l'ÉCHELLE), JAMAIS
// l'inverse : ce module ne fait AUCUN calcul métier.
//
// SÉPARATION DES RESPONSABILITÉS (essentielle) :
//  • core-money = comment l'argent est REPRÉSENTÉ et CALCULÉ (entier, échelle, arrondi §30.3).
//    Il ne produit JAMAIS de chaîne humaine.
//  • money-format = comment l'argent est MONTRÉ à un humain (séparateurs, symbole, signe).
//    Il ne CALCULE JAMAIS et n'ARRONDIT JAMAIS lui-même : réduire des décimales pour l'affichage
//    (ex. PMP échelle 4 → 2 déc.) DÉLÈGUE l'arrondi à `rescale` de core-money → l'affichage
//    utilise la MÊME règle d'arrondi que le calcul, impossible de diverger.
//  ⚠️ Volontairement HORS du cœur fiscal : rien ici n'entre dans un montant signé (NF525) ;
//    garder le cœur minimal et auditable (le formatage i18n grossira pour des raisons marché,
//    pas fiscales → cadence et périmètre d'audit distincts).
//
// Entrée = TOUJOURS l'unité mineure ENTIÈRE (centimes), jamais un flottant euros — la conversion
// euros→centimes (dette du code hérité) reste à la charge de l'appelant adaptateur (ui-native `fmtMoney`).
// Formatage MANUEL (zéro `Intl`) : `Intl` n'est pas fiable sous Hermes (React Native).
// ============================================================================
import { rescale } from '@orgazone/core-money';

/** Options de rendu. Tout est optionnel → défaut = euro FR sans symbole ni séparateur de milliers. */
export interface MoneyFormatOptions {
  /** Décimales de la valeur EN ENTRÉE (défaut 2 = centimes). */
  scale?: number;
  /** Décimales AFFICHÉES (défaut = `scale`). Si < `scale`, l'arrondi est délégué à core-money. */
  displayScale?: number;
  /** Séparateur décimal (défaut « , » — FR). */
  decimalSep?: string;
  /** Séparateur de milliers (défaut « » — aucun ; « espace » pour le style FR des écrans). */
  thousandsSep?: string;
  /** Symbole devise (défaut « » — aucun ; « € », « $ »…). */
  symbol?: string;
  /** Symbole APRÈS le nombre (+ espace, style « 69,99 € ») si true ; AVANT (collé, « $69.99 ») si false. Défaut true. */
  symbolAfter?: boolean;
}

/**
 * Rend une valeur en unité mineure entière (centimes) en chaîne affichable. Ne LÈVE que sur une
 * entrée non entière (contrat : l'argent est toujours entier — un flottant est un bug d'appelant).
 */
export function formatMinor(minor: number, opts: MoneyFormatOptions = {}): string {
  if (!Number.isInteger(minor)) {
    throw new Error('money-format: valeur en unité mineure ENTIÈRE attendue (centimes, jamais un flottant euros)');
  }
  const scale = opts.scale ?? 2;
  const displayScale = opts.displayScale ?? scale;
  if (scale < 0 || displayScale < 0) throw new Error('money-format: échelles ≥ 0 attendues');

  // Passage à l'échelle d'affichage. Réduction (moins de décimales) ⇒ ARRONDI half-up DÉLÉGUÉ à
  // core-money (jamais réinventé ici) ; montée ⇒ zéros exacts. Même échelle ⇒ aucune transformation.
  const shown = displayScale === scale ? minor : rescale(minor, scale, displayScale);

  const decimalSep = opts.decimalSep ?? ',';
  const thousandsSep = opts.thousandsSep ?? '';
  const symbol = opts.symbol ?? '';
  const symbolAfter = opts.symbolAfter ?? true;

  const neg = shown < 0;
  const abs = Math.abs(shown);
  const divisor = 10 ** displayScale;
  // Split ENTIER (modulo exact sur les entiers < 2^53) — pas de division flottante.
  const fracPart = abs % divisor;
  const intPart = (abs - fracPart) / divisor;

  let intStr = String(intPart);
  if (thousandsSep) intStr = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  const body = displayScale > 0 ? `${intStr}${decimalSep}${String(fracPart).padStart(displayScale, '0')}` : intStr;
  const signed = (neg ? '-' : '') + body;

  if (!symbol) return signed;
  return symbolAfter ? `${signed} ${symbol}` : `${symbol}${signed}`;
}

// ============================================================================
// LA DEVISE COURANTE DE LA SURFACE — posée UNE fois au démarrage, lue partout.
//
// ⚠️ POURQUOI UN ÉTAT DE MODULE, ce qui est d'ordinaire un défaut. La devise est une CONSTANTE du
// processus : un back-office sert un magasin à la fois, une caisse appartient à un seul magasin. La
// passer en paramètre voudrait dire la faire traverser ~320 sites d'appel qui n'ont rien à en savoir
// — et il suffirait d'en oublier UN pour qu'un montant s'affiche dans la mauvaise unité, sans que
// rien ne le signale. Un seul point de vérité vaut mieux que trois cents occasions de se tromper.
//
// ⚠️ ELLE VAUT L'EURO TANT QUE PERSONNE NE L'A POSÉE, et ce n'est pas un repli commode : c'est la
// vérité de la totalité des magasins d'aujourd'hui. Une surface qui ne la déclare pas affiche donc
// juste — et le jour où elle sert un magasin en francs suisses, c'est la DÉCLARATION qui manque, pas
// le formatage.
// ============================================================================

/** Ce qu'il faut savoir d'une devise pour l'AFFICHER — jamais pour calculer avec. */
export interface Currency {
  /** Code ISO 4217 (« EUR », « CHF », « JPY »). */
  code: string;
  /** Décimales de la devise — 2 pour l'euro, 0 pour le yen, 3 pour les dinars. */
  scale: number;
  symbol: string;
  symbolAfter?: boolean;
  decimalSep?: string;
  thousandsSep?: string;
}

const EURO: Currency = { code: 'EUR', scale: 2, symbol: '€', symbolAfter: true };
let currentOne: Currency = EURO;

/**
 * LES SYMBOLES ET LA PONCTUATION par devise — ce que le CODE ISO ne dit pas.
 *
 * ⚠️ ICI ET PAS DANS UNE APPLICATION : l'écran du back-office et le bon de commande que son serveur
 *    imprime doivent écrire le même montant de la même façon. Deux tables finiraient par séparer le
 *    document de l'écran d'où on l'a envoyé.
 * ⚠️ PAS EN BASE : la base porte ce qui décide du SENS des montants (le code et la fraction) ; ceci ne
 *    décide que de leur aspect. Une coquille de symbole se corrige par une livraison, sans toucher un
 *    magasin — une fraction fausse, elle, fausserait des prix.
 */
const LOOKS: Readonly<Record<string, Pick<Currency, 'symbol' | 'symbolAfter' | 'decimalSep' | 'thousandsSep'>>> = {
  EUR: { symbol: '€' },
  CHF: { symbol: 'CHF', thousandsSep: '’', decimalSep: '.' },
  GBP: { symbol: '£', symbolAfter: false },
  USD: { symbol: '$', symbolAfter: false, decimalSep: '.', thousandsSep: ',' },
  CAD: { symbol: '$', symbolAfter: false, decimalSep: '.', thousandsSep: ',' },
  JPY: { symbol: '¥', symbolAfter: false },
};

/**
 * La devise d'un magasin, prête à rendre ses montants — d'après son code ISO et sa fraction.
 * ⚠️ UN CODE ABSENT DE LA TABLE AFFICHE SON CODE ISO (« 1 250 SEK »), jamais un symbole inventé ni un
 *    montant nu : c'est moins joli, c'est exact, et ça se voit — donc ça se corrige.
 * ⚠️ Les milliers sont séparés par une espace ORDINAIRE par défaut : c'est le caractère que toute police
 *    sait écrire, y compris celle d'un PDF sans police embarquée.
 */
export function currencyFor(code: string, fraction: number): Currency {
  const iso = code.trim().toUpperCase() || 'EUR';
  return { code: iso, scale: fraction, symbolAfter: true, thousandsSep: ' ', ...(LOOKS[iso] ?? { symbol: iso }) };
}

/**
 * Déclare la devise de CETTE surface. À appeler une fois, au démarrage.
 * ⚠️ Une échelle non entière ou négative est REFUSÉE plutôt que corrigée : elle viendrait d'une
 * configuration cassée, et l'accepter en silence ferait afficher des montants faux pour toujours.
 */
export function setCurrentCurrency(d: Currency): void {
  if (!Number.isInteger(d.scale) || d.scale < 0 || d.scale > 4) {
    throw new Error(`money-format: échelle de devise invalide (${d.scale}) — un entier de 0 à 4 est attendu`);
  }
  currentOne = { symbolAfter: true, ...d };
}

/** La devise en vigueur — l'euro tant qu'aucune n'a été déclarée. */
export function currentCurrency(): Currency {
  return currentOne;
}

/**
 * Rend un montant DANS LA DEVISE COURANTE — « 69,99 € », « 1 250 ¥ », « 12,345 DT ».
 *
 * ⚠️ ELLE S'APPELAIT `formatEur` JUSQU'AU 2026-08-25, et le nom a cessé d'être vrai le jour où elle a
 * rendu la devise du magasin : un `formatEur` qui écrit « 1 250 CHF » est exactement l'écart qu'on
 * refuse à l'écran, transposé dans le code. Renommée avec ses trois sœurs — `parseEur` → `parseMoney`,
 * `fmtEur` → `fmtMoney`, `eurCents` → `moneyMinor` — par un remplacement mécanique de 290 sites que le
 * typage vérifie intégralement.
 * ⚠️ `opts` GARDE LE DERNIER MOT : les appels qui imposent une échelle (prix d'achat en échelle fine)
 * ou un séparateur continuent de valoir exactement ce qu'ils valaient.
 */
/**
 * UN MONTANT COMPACT DANS LA DEVISE COURANTE — « 240 € », « 4,8 k€ », « 156 k€ » : les graduations d'un axe.
 * ⚠️ En MILLIERS au-delà de mille unités, à une décimale jusqu'à cent mille, sans décimale ensuite — un axe se lit d'un
 *    coup d'œil, et « 34 213 € » sur cinq graduations ferait la largeur de la carte.
 * ⚠️ L'ÉCHELLE EST CELLE DE LA DEVISE, et l'arrondi celui de `core-money` : un appelant qui divise par cent afficherait
 *    cent fois trop en yens.
 */
export function formatMoneyCompact(minor: number): string {
  const c = currentOne;
  const symbolAfter = c.symbolAfter ?? true;
  const base = { decimalSep: c.decimalSep ?? ',', thousandsSep: c.thousandsSep ?? ' ', symbolAfter };
  const units = rescale(minor, c.scale, 0);
  if (Math.abs(units) < 1000) return formatMinor(units, { ...base, scale: 0, symbol: c.symbol });
  // ⚠️ « 4,8 k€ » quand le symbole suit le nombre, « $4,8k » quand il le précède — jamais « $k4,8 ».
  const inK = (n: number, scale: number) => (symbolAfter
    ? formatMinor(n, { ...base, scale, symbol: `k${c.symbol}` })
    : `${formatMinor(n, { ...base, scale, symbol: c.symbol })}k`);
  return Math.abs(units) < 100000 ? inK(rescale(minor, c.scale + 3, 1), 1) : inK(rescale(minor, c.scale + 3, 0), 0);
}

export function formatMoney(cents: number, opts: MoneyFormatOptions = {}): string {
  return formatMinor(cents, {
    scale: currentOne.scale,
    symbol: currentOne.symbol,
    symbolAfter: currentOne.symbolAfter ?? true,
    ...(currentOne.decimalSep ? { decimalSep: currentOne.decimalSep } : {}),
    ...(currentOne.thousandsSep ? { thousandsSep: currentOne.thousandsSep } : {}),
    ...opts,
  });
}

// ============================================================================
// LA LECTURE — le SYMÉTRIQUE du rendu : ce qu'un humain tape → l'unité mineure entière.
//
// ⚠️ ELLE VIT ICI, ET C'EST LA DETTE ANNONCÉE EN TÊTE DE FICHIER QUI SE SOLDE. L'en-tête disait
// « la conversion euros→centimes reste à la charge de l'appelant » : chaque appelant l'a donc
// réécrite, et chacun avec ses propres pièges. Or LIRE est exactement l'inverse de MONTRER — même
// séparateur décimal, même échelle, mêmes bornes. Les séparer garantissait qu'un aller-retour ne
// boucle pas.
//
// ⚠️ AUCUN FLOTTANT SUR LE CHEMIN, jamais. `parseFloat('69.99') * 100` rend 6998.999999999999, donc
// 6998 après troncature : un centime évaporé, sur un prix de vente, qui finira sur un ticket fiscal.
// On découpe la CHAÎNE en partie entière et décimales, et on n'assemble que des entiers.
//
// ⚠️ LES DÉCIMALES EN TROP SONT REFUSÉES, jamais arrondies en silence. Arrondir « 12,345 » à 12,35
// change ce que le client paiera sans que personne ne l'ait demandé, et l'opérateur croira avoir
// saisi ce qu'il a tapé. Un refus se corrige ; un arrondi silencieux ne se voit jamais.
// ⚠️ C'est la SEULE différence de doctrine avec le rendu, qui lui a le droit d'arrondir (il délègue
// à `rescale`) : montrer moins de décimales ne change pas la valeur, en lire moins la changerait.
// ============================================================================

/** Ce qu'une saisie de montant peut valoir : un entier à l'échelle demandée, rien, ou un refus NOMMÉ. */
export type ParsedMoney =
  | { state: 'empty' }
  | { state: 'ok'; minor: number }
  | { state: 'rejected'; reason: string };

export interface MoneyParseOptions {
  /** Décimales acceptées. Défaut 2 (centimes) ; 4 pour l'échelle fine des prix d'achat. */
  scale?: number;
  /** Séparateurs décimaux tolérés. Défaut : la virgule ET le point — on ne corrige pas l'opérateur. */
  decimalSeps?: string[];
}

/**
 * Lit un montant tapé et rend l'ENTIER en unité mineure.
 * ⚠️ La VIRGULE est le séparateur français : refuser « 69,99 » au motif que JavaScript veut un point
 * ferait accuser l'opérateur d'une convention qui n'est pas la sienne.
 */
export function parseMinor(input: string, opts: MoneyParseOptions = {}): ParsedMoney {
  // ⚠️ LE DÉFAUT SUIT LA DEVISE COURANTE (2026-08-25), il ne vaut plus 2 en dur. Dans un magasin en
  // yens, un champ de prix doit REFUSER « 12,50 » — l'accepter puis l'arrondir changerait ce que le
  // client paiera. Les appels qui imposent une échelle (prix d'achat à l'échelle fine) sont intacts.
  const scale = opts.scale ?? currentOne.scale;
  if (!Number.isInteger(scale) || scale < 0) throw new Error('money-format: échelle ENTIÈRE ≥ 0 attendue');

  // Espaces retirés PARTOUT : un copier-coller depuis un tableau colle « 1 299,00 » avec sa fine.
  const t = input.replace(/[\s\u00a0\u202f]/g, '');
  if (!t) return { state: 'empty' };

  // ⚠️ ON NE CONSTRUIT PAS DE CLASSE DE CARACTÈRES : un séparateur exotique y demanderait un
  // échappement, et une expression régulière assemblée à la main est un piège de plus. On NORMALISE
  // les séparateurs acceptés vers un point unique, puis on lit une forme fixe.
  const seps = opts.decimalSeps ?? [',', '.'];
  let normalise = t;
  for (const sep of seps) normalise = normalise.split(sep).join('.');
  const m = /^(-?)(\d+)(?:\.(\d*))?$/.exec(normalise);
  if (!m) return { state: 'rejected', reason: 'un montant s’écrit avec des chiffres et une virgule (ex. 69,99)' };

  const signe = m[1] === '-' ? -1 : 1;
  const entiere = m[2]!;
  const frac = m[3] ?? '';
  if (frac.length > scale) {
    return {
      state: 'rejected',
      reason: `${scale} décimale${scale > 1 ? 's' : ''} au maximum (« ${t} » en compte ${frac.length})`,
    };
  }
  // ⚠️ On COMPLÈTE les décimales manquantes plutôt que de multiplier : « 69,9 » vaut 69,90, et
  // l'assemblage reste une opération sur des CHAÎNES puis un seul `Number` sur un entier.
  const minor = signe * Number(entiere + frac.padEnd(scale, '0'));
  if (!Number.isSafeInteger(minor)) return { state: 'rejected', reason: 'montant hors des limites représentables' };
  return { state: 'ok', minor };
}

/**
 * UN MONTANT LU DANS UNE CELLULE DE FICHIER (import d'articles) — l'entier à l'échelle demandée, ou `null` s'il n'est
 * pas lisible.
 * ⚠️ UNE SEULE RÈGLE POUR L'ÉCRAN ET LE SERVEUR : l'écran juge avant l'envoi, le serveur écrit — deux règles laissaient
 *    passer à l'écran ce que le serveur refusait (« -5 ») et refusaient ce qu'il acceptait (« 1 250,00 »).
 * ⚠️ LE SYMBOLE MONÉTAIRE ET LES ESPACES SE TOLÈRENT (un export comptable les colle) ; un montant NÉGATIF est refusé
 *    (un prix ne l'est jamais), et un montant à plus de décimales que l'échelle aussi — l'arrondir changerait le prix.
 */
export function parseCellAmount(raw: string, scale: number): number | null {
  const r = parseMinor(raw.replace(/[€$£¥¤]/g, '').replace(/^\s*(EUR|CHF|USD|GBP)\s*|\s*(EUR|CHF|USD|GBP)\s*$/gi, ''), { scale });
  return r.state === 'ok' && r.minor >= 0 ? r.minor : null;
}

/** Préréglage EURO : « 69,99 » → 6999. */
export function parseMoney(input: string): ParsedMoney {
  return parseMinor(input, { scale: currentOne.scale });
}

/**
 * L'unité mineure vers le texte qu'on met DANS un champ de saisie.
 *
 * ⚠️ DISTINCT DE `formatMinor`, et c'est délibéré : celui-ci rend pour l'ŒIL (symbole, séparateur de
 * milliers), celui-là pour un `<input>`. Un « 1 299,00 € » recopié tel quel dans un champ serait refusé
 * à la relecture — un aller-retour doit BOUCLER, sinon le champ n'est pas réutilisable.
 */
export function toInputValue(minor: number | null, scale = currentOne.scale): string {
  if (minor === null) return '';
  const neg = minor < 0;
  const abs = Math.abs(minor).toString().padStart(scale + 1, '0');
  const entiere = abs.slice(0, abs.length - scale);
  const frac = scale > 0 ? abs.slice(abs.length - scale) : '';
  return `${neg ? '-' : ''}${entiere}${scale > 0 ? `,${frac}` : ''}`;
}
